"use client";

import { useAuth } from "@/context/AuthContext";
import { useParams, useApiReady } from "@/hooks";
import { AppDispatch } from "@/store";
import { clearDashboard } from "@/store/modules/dashboard/slice";
import { fetchDashboardStats } from "@/store/modules/dashboard/thunks";
import { UseDataRangeFilter } from "@/types";
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

const isValidDate = (date: unknown): date is Date => {
  return date instanceof Date && !isNaN(date.getTime());
};

const formatDateToString = (date: Date | undefined): string => {
  if (!date || !isValidDate(date)) return "";
  return date.toISOString().split("T")[0];
};

const useDashboard = ({ dateRange, onSuccess }: UseDataRangeFilter) => {
  const { params } = useParams();
  const { currency } = useAuth();
  const { isReady: isApiReady } = useApiReady();
  const { dashboard, loading, error } = useSelector(
    (state: any) => state.dashboard,
  );
  const dispatch: AppDispatch = useDispatch();
  const { data: session } = useSession();
  const establishmentSlug = session?.user?.user?.establishment?.slug;

  const isInitialMount = useRef(true);
  const previousParamsRef = useRef<string>("");

  const startDateString = useMemo(
    () => formatDateToString(dateRange?.start),
    [dateRange?.start?.getTime()],
  );

  const endDateString = useMemo(
    () => formatDateToString(dateRange?.end),
    [dateRange?.end?.getTime()],
  );

  const hasValidDates = startDateString && endDateString;

  const createPayload = useCallback(() => {
    return {
      ...params,
      establishment_slug: establishmentSlug,
      start_date: startDateString,
      end_date: endDateString,
    };
  }, [params, establishmentSlug, startDateString, endDateString]);

  const handleError = useCallback(
    (err: any) => {
      if (err?.errors) {
        Object.values(err.errors)
          .flat()
          .forEach((msg: any) => toast.error(msg));
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Erro ao buscar dados da dashboard.");
      }
      dispatch(clearDashboard());
    },
    [dispatch],
  );

  // Estabilizar params usando JSON.stringify
  const paramsStringified = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    if (!isApiReady || !hasValidDates) return;

    const payload = {
      ...params,
      establishment_slug: establishmentSlug,
      start_date: startDateString,
      end_date: endDateString,
    };

    const paramsString = JSON.stringify(payload);

    if (paramsString === previousParamsRef.current && !isInitialMount.current) {
      return;
    }

    previousParamsRef.current = paramsString;
    isInitialMount.current = false;

    dispatch(fetchDashboardStats(payload as any));
  }, [
    dispatch,
    isApiReady,
    hasValidDates,
    paramsStringified,
    establishmentSlug,
    startDateString,
    endDateString,
  ]);

  const handleSubmitFilter = useCallback(async () => {
    if (!isApiReady || !hasValidDates) return;

    const payload = createPayload();
    previousParamsRef.current = "";

    try {
      await dispatch(fetchDashboardStats(payload)).unwrap();
      onSuccess?.();
    } catch (err: any) {
      handleError(err);
    }
  }, [
    dispatch,
    isApiReady,
    hasValidDates,
    createPayload,
    onSuccess,
    handleError,
  ]);

  return {
    dashboard,
    loading,
    error,
    handleSubmitFilter,
    currency,
  };
};

export default useDashboard;
